import DataTable, { ColumnAlignment } from '@/common/tableData/DataTable';
import { AiOutlineDelete, AiOutlineEdit } from 'react-icons/ai';
import { FormGroup, Input, Label } from 'reactstrap';
import { ReactNode, useEffect, useState } from 'react';
import DebouncedInput from '@/components/DebounceInput';
import { ColumnFilter, SortingState } from '@tanstack/react-table';
import useTournamentTemplates from '@/hooks/useTournamentTemplates';
import ConfirmModal from '@/components/ConfirmationModal/ConfirmModal';
import useTournaments from '@/hooks/useTournaments';
import { toast } from 'react-toastify';
import { useGetTournamentsQuery } from '@/redux/api/tournaments';

const TournamentTemplateTable = ({ onSelectTemplate }: { onSelectTemplate: (templateId: string) => void }) => {
  const { tournamentTemplates, updateTournamentTemplate, refetchTournamentTemplates } = useTournamentTemplates();
  const { data: tournaments } = useGetTournamentsQuery({ sng: true });

  useEffect(() => {
    refetchTournamentTemplates();
  }, []);

  const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([
    {
      id: 'includeTournaments',
      value: true,
    },
    {
      id: 'includeSatellites',
      value: true,
    },
  ]);

  const [sorting, setSorting] = useState<SortingState>([
    {
      id: 'name',
      desc: true,
    },
  ]);

  const [confirmModalData, setConfirmModalData] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    id: string | null;
    type: string;
    body?: ReactNode | (() => ReactNode);
  } | null>(null);

  const toggleColumnFilter = (filterId: string) => {
    setColumnFilters((filters) => {
      const newFilters = filters.filter((filter) => filter.id !== filterId);
      return [...newFilters, { id: filterId, value: !filters.find((filter) => filter.id === filterId)?.value }];
    });
  };

  const handleConfirmAction = () => {
    if (confirmModalData?.type === 'delete') {
      handleDeleteTemplate();
      return;
    }
  };

  const handleDeleteTemplate = async () => {
    if (!confirmModalData?.id) {
      return;
    }

    const activeTournamentsWithTemplate =
      tournaments?.data?.filter((tournament) => tournament.templateId === confirmModalData.id) ?? [];

    if (activeTournamentsWithTemplate?.length > 0) {
      toast.error('Cannot archive template with active tournaments');
      return;
    }

    await updateTournamentTemplate(
      { id: confirmModalData.id, deletedAt: new Date() },
      {
        onSuccess: () => {
          toast.success('Template archived successfully');
          setConfirmModalData(null);
        },
        onError: () => {
          toast.error('Failed to archived template');
        },
      },
    );
  };

  const onDeleteTemplate = (templateId: string) => {
    setConfirmModalData({
      isOpen: true,
      title: 'Archive Template',
      message: 'Are you sure you want to archive this template?',
      id: templateId,
      type: 'delete',
    });
  };

  const handleTemplateNameSearch = (value: string) => {
    setColumnFilters((filters) => {
      const newFilters = filters.filter((filter) => filter.id !== 'name');
      const existingNameFilter = filters.find((filter) => filter.id === 'name');
      if (existingNameFilter && value !== '') {
        return [
          ...newFilters,
          {
            id: 'name',
            value: value,
          },
        ];
      }

      if (existingNameFilter && value === '') {
        return newFilters;
      }

      if (!existingNameFilter && value !== '') {
        return [
          ...newFilters,
          {
            id: 'name',
            value: value,
          },
        ];
      }

      return filters;
    });
  };

  if (!tournamentTemplates) {
    return null;
  }

  return (
    <div className="d-flex flex-column gap-3 py-2">
      {confirmModalData?.isOpen && (
        <ConfirmModal
          title={confirmModalData.title}
          message={confirmModalData.message}
          body={confirmModalData.body}
          onConfirm={handleConfirmAction}
          disableConfirm={confirmModalData?.type === 'notify'}
          onClose={() => setConfirmModalData(null)}
        />
      )}
      <div className="w-100 d-flex justify-content-between align-items-center py-2">
        <DebouncedInput
          className="form-control w-auto"
          placeholder="Search"
          isLoading={false}
          onChange={(val) => handleTemplateNameSearch(val as string)}
          value={columnFilters.find((filter) => filter.id === 'name')?.value as string}
        />
      </div>
      <DataTable
        data={tournamentTemplates}
        columnFilters={columnFilters}
        sorting={sorting}
        onSortingChange={setSorting}
        columns={[
          {
            header: 'Name',
            accessorKey: 'name',
            size: 600,
          },
          {
            header: 'Game',
            accessorKey: 'game',
            size: 120,
          },
          {
            header: 'Max Players',
            accessorKey: 'maxPlayers',
            size: 120,
            meta: {
              alignment: ColumnAlignment.RIGHT,
            },
          },
          {
            header: 'Min Players',
            accessorKey: 'minPlayers',
            size: 120,
            meta: {
              alignment: ColumnAlignment.RIGHT,
            },
          },
          {
            header: 'Actions',
            cell: ({ row }) => (
              <div className="d-flex justify-content-center flex-row gap-2">
                <div className="d-flex justify-content-start w-full flex-row">
                  <AiOutlineDelete
                    className="cursor-pointer"
                    onClick={() => onDeleteTemplate(row.original.id)}
                    size={20}
                  />
                </div>
                <div className="d-flex justify-content-start w-full flex-row">
                  <AiOutlineEdit
                    className="cursor-pointer"
                    onClick={() => onSelectTemplate(row.original.id)}
                    size={20}
                  />
                </div>
              </div>
            ),
            meta: {
              alignment: ColumnAlignment.CENTER,
            },
          },
        ]}
      />
    </div>
  );
};

export default TournamentTemplateTable;
